<template>

    <div class="escalation-offer">

        <div class="escalation-decision">

            <v-btn
                @click="handleAcceptEscalation"
                variant="outlined"
                :class="isSelectedDecision(decisionEscalate) || isEscalationInProgress ? 'selected' : ''"
                :disabled="disableActionButtons"
                :loading="acceptInProgress"
            >{{ $t(escalationOptionButtonText) }}</v-btn>

            <v-btn
                @click="handleDeclineEscalation"
                color="secondary"
                :class="isSelectedDecision(decisionContinue) ? 'selected' : ''"
                :disabled="disableActionButtons"
                :loading="declineInProgress"
            >{{$t("CONTINUE_CHATTING_0")}}</v-btn>

        </div>       
        
        <div class="escalation-form" v-if="showEscalationForm">

            <div class="form-header">
                <h3>{{ escalationOptionFormHeader }}</h3>
            </div>

            <EscalationForm :callbackFunction="handleTicketEscalation" :escalationOption="escalationOption" @exitForm="closeEscalationForm"></EscalationForm>
        </div>

        <v-card v-if="escalationLink.url" variant="tonal" class="escalation-link">
            <v-card-text>
                <p>{{$t("TICKET_OPENED_TITLE")}}</p>
                <p>{{$t("TICKET_OPENED_BODY")}}</p>
                <a :href="escalationLink.url" rel="noopener" target="_blank">
                    {{ escalationLink.text }}
                </a>
            </v-card-text>
        </v-card>

    </div>

</template>

<script>
    import EscalationForm from "./escalationform.vue";
    import { mapActions, mapGetters } from "vuex";

    export default {
        name: "EscalationOffer",
        props: {
            chatId: {
                type: Number,
                required: true
            },
            chatMessageId: {
                type: Number,
                required: true
            },
            escalationOption: {
                type: String,
                required: true
            },
            currentDecision: {
                type: String,
                required: true
            },
            updateDecisionCallback: {
                type: Function,
                required: true
            }
        },
        components: {
            EscalationForm
        },
        data: () => ({
            valid: false,
            inProgress: false,
            acceptInProgress: false,
            declineInProgress: false,
            escalationInProgress: false,
            showEscalationForm: false,
            escalationDecision: "",
            escalationLink: {
                url: "",
                text: ""
            },
        }),
        beforeMount() {
            this.escalationDecision = this.$const?.ESCALATION_DECISION__NONE;
        },
        mounted() {
            this.updateDecisionCallback(this.currentDecision);
        },
        updated() {
            this.scrollToBottom();
        },
        computed: {
            ...mapGetters([
                "escalationPending",
                "livechatApp",
                "currentChat",
            ]),
            escalationOptionButtonText() {
                switch (this.escalationOption) {
                    case this.$const.ESCALATION_OPTION__COMMUNITY:
                        return "GO_TO_COMMUNITY";
                    case this.$const.ESCALATION_OPTION__TICKET:
                        return "OPEN_A_TICKET";
                    case this.$const.ESCALATION_OPTION__LIVECHAT:
                        return "START_LIVE_CHAT";
                    default:
                        return "GO_TO_COMMUNITY";
                }
            },
            escalationOptionFormHeader() {
                switch (this.escalationOption) {
                    case "ticket":
                        return this.$t('ESCALATION_FORM_TITLE_TICKET');
                    case "livechat":
                        return this.$t('ESCALATION_FORM_TITLE_LIVECHAT');
                    default:
                        return "";
                }
            },
            requestInProgress() {
                return this.acceptInProgress || this.declineInProgress;
            },
            isEscalationInProgress() {
                return this.escalationInProgress;
            },
            decisionSubmitted() {
                return this.currentDecision !== this.$const.ESCALATION_DECISION__NONE
            },
            disableActionButtons() {
                return this.decisionSubmitted || this.requestInProgress || this.isEscalationInProgress;
            },

            // consts
            decisionNone() {
                return this.$const.ESCALATION_DECISION__NONE;
            },
            decisionContinue() {
                return this.$const.ESCALATION_DECISION__CONTINUE;
            },
            decisionEscalate() {
                return this.$const.ESCALATION_DECISION__ESCALATE;
            },
        },
        methods: {
            ...mapActions([
                "incrementAppLoader",
                "decrementAppLoader",
                "setEscalationPending",
                "escalateChat",
                "declineEscalation",
                "getLiveChatApp",
                "setChatData",
            ]),
            async handleAcceptEscalation(e){

                e.preventDefault();

                this.setAcceptInProgress(true);
                this.setEscalationInProgress(true);

                switch (this.escalationOption) {

                    case this.$const.ESCALATION_OPTION__COMMUNITY:
                        
                        await this.handleCommunityEscalation();
                        break;
                        
                    case this.$const.ESCALATION_OPTION__TICKET:

                        this.showEscalationForm = true;
                        break;

                    case this.$const.ESCALATION_OPTION__LIVECHAT:

                        await this.handleLiveChatEscalation();
                        break;

                    default:
                        return false;
                }

                this.setAcceptInProgress(false);
            },

            async handleCommunityEscalation() {

                window.open("https://community.ezoic.com");

                const payload = {
                    chatId: this.chatId,
                    chatMessageId: this.chatMessageId,
                    escalationOption: this.escalationOption
                }

                const result = await this.escalateChat(payload);
                if (result) {
                    this.updateDecisionCallback(this.$const.ESCALATION_DECISION__ESCALATE);
                    this.setEscalationInProgress(false);
                    this.setEscalationPending(false);

                    this.setChatData(result.chat);
                }
            },

            async handleTicketEscalation(formData) {

                const payload = {
                    chatId: this.chatId,
                    chatMessageId: this.chatMessageId,
                    escalationOption: this.escalationOption,
                    escalationForm: formData,
                }

                payload.escalationForm.chatId = this.chatId;

                const result = await this.escalateChat(payload);
                if (result) {
                    const ticketLink = result.escalationLink;
                    const linkText = this.$t('ESCALATION_FORM_RESULT_LINK_TEXT_TICKET');
                    
                    this.showEscalationForm = false;
                    this.setEscalationLink(ticketLink, linkText);

                    this.updateDecisionCallback(this.$const.ESCALATION_DECISION__ESCALATE);
                    this.setEscalationInProgress(false);
                    this.setEscalationPending(false);

                    this.setChatData(result.chat);
                }
            },

            async handleLiveChatEscalation() {

                // open live chat
                const chatId = this.chatId;
                
                if (!this.livechatApp.isLoaded) {
                    this.incrementAppLoader();
                    await this.getLiveChatApp()
                    this.decrementAppLoader();
                }

                const lc = this.livechatApp.app;

                if (!lc.args.lhc_var.proteus_chat_id) {
                    lc.setAttribute('proteus_chat_id', chatId);
                }
                if (this.currentChat?.domain) {
                  lc.setAttribute('domain', this.currentChat.domain);
                }

                lc.open();

                // update decision & close chat
                const payload = {
                    chatId: this.chatId,
                    chatMessageId: this.chatMessageId,
                    escalationOption: this.escalationOption
                }

                const result = await this.escalateChat(payload);
                if (result) {
                    this.updateDecisionCallback(this.$const.ESCALATION_DECISION__ESCALATE);
                    this.setEscalationInProgress(false);
                    this.setEscalationPending(false);

                    this.setChatData(result.chat);
                }
            },

            async handleDeclineEscalation(e) {

                e.preventDefault();
                this.setDeclineInProgress(true);

                const payload = {
                    chatId: this.chatId,
                    chatMessageId: this.chatMessageId,
                }

                const result = await this.declineEscalation(payload);
                if (result) {
                    this.updateDecisionCallback(this.$const.ESCALATION_DECISION__CONTINUE);
                    this.setEscalationInProgress(false);
                    this.setEscalationPending(false);
                }

                this.setDeclineInProgress(false);
            },
            handleFollowEscalationLink() {
                if (!this.escalationLink.url) {
                    return;
                }

                window.open(this.escalationLink.url);
            },
            isSelectedDecision(decision) {
                return this.currentDecision === decision;
            },
            setAcceptInProgress(inProgress) {
                this.acceptInProgress = inProgress;
            },
            setDeclineInProgress(inProgress) {
                this.declineInProgress = inProgress;
            },
            setEscalationInProgress(value) {
                this.escalationInProgress = value;
            },
            closeEscalationForm() {
                this.setEscalationInProgress(false);
                this.showEscalationForm = false;
            },
            scrollToBottom() {
                const scrollToPosition = (document.body.scrollHeight + 1000) || (document.documentElement.scrollHeight + 1000);
                window.scrollTo(0, scrollToPosition);
            },
            setEscalationLink(linkUrl, linkText) {
                this.escalationLink = {
                    url: linkUrl,
                    text: linkText
                };
            }
        },
    }

</script>